import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DecideDocumentRequestDto, DocumentRequestDto } from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';

export const documentRequestKeys = {
  byFile: (fileId: string) => ['document-requests', fileId] as const,
};

export async function listDocumentRequests(fileId: string): Promise<DocumentRequestDto[]> {
  const { data } = await api.get<DocumentRequestDto[]>(`/files/${fileId}/document-requests`);
  return data;
}

export function useDocumentRequests(fileId: string) {
  return useQuery({
    queryKey: documentRequestKeys.byFile(fileId),
    queryFn: () => listDocumentRequests(fileId),
    enabled: !!fileId,
  });
}

export function useDecideDocumentRequest(fileId: string) {
  const queryClient = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async ({ requestId, ...dto }: DecideDocumentRequestDto & { requestId: string }) => {
      const { data } = await api.post<DocumentRequestDto[]>(
        `/files/${fileId}/document-requests/${requestId}/decide`,
        dto,
      );
      return data;
    },
    onSuccess: (data, { decision }) => {
      queryClient.setQueryData(documentRequestKeys.byFile(fileId), data);
      // The dashboard's open-request counts are now stale — a decision is
      // exactly what removes a file from that column.
      queryClient.invalidateQueries({ queryKey: ['files'] });
      // Sending puts a document in front of a member of the public; the officer
      // should be told it went, not left to infer it from a chip changing.
      toast.success(
        decision === 'send'
          ? 'gov.documentRequests.sentToast'
          : 'gov.documentRequests.refusedToast',
      );
    },
    onError: (err) => toast.error(err, 'gov.documentRequests.decideError'),
  });
}
